import { Stack, Typography } from '@mui/material';
import { isCPFOrCNPJ } from 'brazilian-values';
import { useFormik, FormikProvider } from 'formik';
import parsePhoneNumberFromString, {
  parsePhoneNumber,
} from 'libphonenumber-js';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';
import * as yup from 'yup';

import {
  Address,
  PhoneInputFormik,
  TaxIDInputFormik,
  TextField,
} from '@woovi/form';
import { useMutationCallbacks } from '@woovi/relay';
import { PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { ActionButton } from '@woovi/ui';
import { registerYupValidations } from '@woovi/utils';

import type { PaymentMethodSelectedCustomerRegisterForm_charge$key } from './__generated__/PaymentMethodSelectedCustomerRegisterForm_charge.graphql.ts';
import type { ChargeCustomerRegisterMutation } from '../customerRegister/mutations/__generated__/ChargeCustomerRegisterMutation.graphql.ts';
import { ChargeCustomerRegister } from '../customerRegister/mutations/ChargeCustomerRegisterMutation.ts';

registerYupValidations();

type PaymentMethodSelectedCustomerRegisterFormProps = {
  charge: PaymentMethodSelectedCustomerRegisterForm_charge$key;
};

type PaymentMethodSelectedCustomerRegisterFormValues = {
  name: string;
  taxID: string;
  email: string;
  phone: string;
  address?: {
    zipcode: string;
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    complement: string;
  };
  paymentMethodSelected: string;
};

const PaymentMethodSelectedCustomerRegisterForm = (
  props: PaymentMethodSelectedCustomerRegisterFormProps,
): ReactNode => {
  const { t } = useTranslation();

  const charge =
    useFragment<PaymentMethodSelectedCustomerRegisterForm_charge$key>(
      graphql`
        fragment PaymentMethodSelectedCustomerRegisterForm_charge on Charge {
          id
          paymentMethodSelected
        }
      `,
      props.charge,
    );

  const [chargeCustomerRegister, isPending] =
    useMutationCallbacks<ChargeCustomerRegisterMutation>({
      name: 'ChargeCustomerRegister',
      mutation: ChargeCustomerRegister,
    });

  const getPhone = (phone: string): { phone?: string } => {
    const phoneWithoutPrefix =
      parsePhoneNumberFromString(phone)?.formatNational();

    if (!phoneWithoutPrefix) {
      return {};
    }

    return { phone: parsePhoneNumber(phone, 'BR').number };
  };

  const formikbag = useFormik<PaymentMethodSelectedCustomerRegisterFormValues>({
    initialValues: {
      name: '',
      taxID: '',
      email: '',
      phone: '',
      paymentMethodSelected: charge.paymentMethodSelected || '',
    },
    validationSchema: yup.object().shape({
      name: yup.string().required(t('Name is required')),
      taxID: yup
        .string()
        .test('isCPF', t('Customer TaxID is invalid'), (value) => {
          if (!value) {
            return true;
          }

          return isCPFOrCNPJ(value);
        })
        .required(t('TaxID is required')),
      email: yup
        .string()
        .email(t('Email must be a valid email'))
        .required(t('Email is required')),
      phone: yup
        .string()
        .required(t('Phone is required'))
        .test(
          'phone',
          t('Phone is invalid'),
          (value) => !!(value && parsePhoneNumber(value, 'BR')?.isValid()),
        ),
      address: yup.object().when('paymentMethodSelected', {
        is: PAYMENT_METHOD_TYPE.BOLETO,
        then: yup.object().shape({
          zipcode: yup.string().required(t('Zipcode is required')),
          street: yup.string().required(t('Street is required')),
          number: yup.string().required(t('Number is required')),
          neighborhood: yup.string().required(t('Neighborhood is required')),
          city: yup.string().required(t('City is required')),
          state: yup.string().required(t('State is required')),
          complement: yup.string(),
        }),
        otherwise: yup.object().nullable(),
      }),
    }),
    validateOnMount: true,
    onSubmit: (values) => {
      const { name, email, phone, taxID, address } = values;

      const getAddress = (): Record<string, unknown> => {
        if (!address) {
          return {};
        }

        return {
          address,
        };
      };

      chargeCustomerRegister({
        variables: {
          input: {
            chargeId: charge.id,
            name,
            email,
            ...getPhone(phone),
            taxID,
            ...getAddress(),
          },
        },
      });
    },
  });

  const { isValid, isSubmitting, handleSubmit } = formikbag;

  const disabled = !isValid || isSubmitting || isPending;
  const loading = isSubmitting || isPending;

  const getAddressFields = (): ReactNode => {
    if (charge?.paymentMethodSelected !== PAYMENT_METHOD_TYPE.BOLETO) {
      return null;
    }

    return (
      <Stack sx={{ gap: 1.5 }}>
        <Typography fontWeight='bold'>{`${t('Address')}:`}</Typography>
        <Stack
          sx={{
            gap: 2,
            // Deal with the field row component inside address
            '.MuiBox-root': {
              gap: 2,
            },
          }}
        >
          <Address
            prefix='address'
            showHeader={false}
            sx={{
              m: 0,
            }}
          />
        </Stack>
      </Stack>
    );
  };

  return (
    <FormikProvider value={formikbag}>
      <Stack sx={{ gap: 1.5 }}>
        <Stack sx={{ gap: 2 }}>
          <TextField
            name='name'
            label={t('Name')}
            placeholder={t('Name')}
            sx={{ m: 0 }}
          />
          <TaxIDInputFormik
            name='taxID'
            label={t('CPF or CNPJ')}
            placeholder={t('CPF or CNPJ')}
            sx={{ m: 0 }}
          />
          <TextField
            name='email'
            label={t('Email')}
            placeholder={t('Email')}
            sx={{ m: 0 }}
          />
          <PhoneInputFormik
            name='phone'
            label={t('Phone')}
            type='tel'
            defaultCountry='br'
            onlyCountries={['br']}
            sx={{ m: 0 }}
          />
          {getAddressFields()}
        </Stack>
        <ActionButton
          type='submit'
          disabled={disabled}
          loading={loading}
          onClick={handleSubmit}
          sx={(theme) => ({
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
            },
            '&:focus': {
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
            },
          })}
        >
          {t('Continue')}
        </ActionButton>
      </Stack>
    </FormikProvider>
  );
};

export default PaymentMethodSelectedCustomerRegisterForm;
